import { Rectangle, Vector2 } from "../math";
import { TileCoordinates, TileRectangle } from "./coordinates";
import { direction } from "./movement";
import { TileArea } from "./coordinates/TileArea";
import { deps } from "../../cluetrainer/dependencies";
export var Transportation;
(function (Transportation) {
    let EntityActionMovement;
    (function (EntityActionMovement) {
        let Offset;
        (function (Offset) {
            function transform(offset, transform) {
                return {
                    ...Vector2.snap(Vector2.transform(offset, transform)),
                    level: offset.level,
                };
            }
            Offset.transform = transform;
        })(Offset = EntityActionMovement.Offset || (EntityActionMovement.Offset = {}));
    })(EntityActionMovement = Transportation.EntityActionMovement || (Transportation.EntityActionMovement = {}));
    let TeleportGroup;
    (function (TeleportGroup) {
        let TeleportAccess;
        (function (TeleportAccess) {
            function isAnywhere(access) {
                return access.type == "item" || access.type == "spellbook";
            }
            TeleportAccess.isAnywhere = isAnywhere;
            function interactiveArea(access) {
                return access.interactive_area ?? EntityTransportation.default_interactive_area(TileArea.toRect(access.clickable_area));
            }
            TeleportAccess.interactiveArea = interactiveArea;
        })(TeleportAccess = TeleportGroup.TeleportAccess || (TeleportGroup.TeleportAccess = {}));
        let TeleportProps;
        (function (TeleportProps) {
            function combinePrioritized(...props) {
                return props.reduce((a, b) => ({
                    img: a?.img ?? b?.img,
                    menu_ticks: a?.menu_ticks ?? b?.menu_ticks,
                    animation_ticks: a?.animation_ticks ?? b?.animation_ticks,
                    code: a?.code ?? b?.code,
                    hidden: a?.hidden ?? b?.hidden
                }), {});
            }
            TeleportProps.combinePrioritized = combinePrioritized;
        })(TeleportProps = TeleportGroup.TeleportProps || (TeleportGroup.TeleportProps = {}));
        function canBeAccessedAnywhere(group) {
            return group.access.some(TeleportAccess.isAnywhere);
        }
        TeleportGroup.canBeAccessedAnywhere = canBeAccessedAnywhere;
        class Spot {
            constructor(group, spot, access) {
                this.group = group;
                this.spot = spot;
                this.access = access;
                this.customization = null;
                this.visual_access = null;
                this.visual_access = access ?? this.group.access.find(TeleportGroup.TeleportAccess.isAnywhere);
                this.refresh();
            }
            refresh() {
                this.customization = deps().app.settings.active_teleport_customization.value();
                const pota = this.visual_access && this.visual_access.type == "item" && this.visual_access.can_be_in_pota
                    ? this.customization.pota_slots.find((p) => p.jewellry.group_id == this.group.id)
                    : null;
                this.pota_slot = pota ? {
                    img: { url: `pota_${pota.pota.color}.png` },
                    code_prefix: `${pota.pota.slot},`
                } : null;
                // Props are combined from the various ways they can be specified.
                // Prop definitions for Access x Spot have the highest priority,
                // followed by per-access props, then per-spot props and finally per-group props.
                this.props = TeleportProps.combinePrioritized({ img: this.pota_slot?.img }, this.access?.per_spot_props?.[this.spot.id], this.visual_access, this.spot, this.group, {
                    animation_ticks: 0,
                    menu_ticks: 0,
                    code: "",
                    img: { url: "homeport.png" }
                });
            }
            hover() {
                return (this.group.name && this.spot.name)
                    ? `${this.group.name} - ${this.spot.name}`
                    : this.group.name || this.spot.name;
            }
            image() {
                return this.props.img;
            }
            code() {
                let base_code = this.props.code;
                if (this.group.id == "fairyring") {
                    const i = deps().app.settings.active_teleport_customization.value().fairy_ring_favourites.indexOf(this.spot.id);
                    if (i >= 0)
                        base_code = ((i + 1) % 10).toString();
                }
                return (this.pota_slot?.code_prefix ?? "") + base_code;
            }
            centerOfTarget() {
                return TileArea.activate(this.spot.target).center(true, false);
            }
            targetArea() {
                return this.spot.target;
            }
            id() {
                return {
                    group: this.group.id,
                    spot: this.spot.id
                };
            }
        }
        TeleportGroup.Spot = Spot;
    })(TeleportGroup = Transportation.TeleportGroup || (Transportation.TeleportGroup = {}));
    let EntityAction;
    (function (EntityAction) {
        function findApplicable(action, tile) {
            return action.movement.find(movement => {
                return !movement.valid_from || TileArea.activate(movement.valid_from).query(tile);
            });
        }
        EntityAction.findApplicable = findApplicable;
        function interactiveArea(entity, action) {
            return action.interactive_area ?? EntityTransportation.defaultInteractiveArea(entity);
        }
        EntityAction.interactiveArea = interactiveArea;
    })(EntityAction = Transportation.EntityAction || (Transportation.EntityAction = {}));
    let EntityTransportation;
    (function (EntityTransportation) {
        function default_interactive_area(area) {
            const extended = TileRectangle.extend(area, 1);
            const tiles = TileArea.activate(TileArea.fromRect(extended, true));
            tiles.set(TileRectangle.tl(extended), false);
            tiles.set(TileRectangle.tr(extended), false);
            tiles.set(TileRectangle.bl(extended), false);
            tiles.set(TileRectangle.br(extended), false);
            tiles.save();
            return tiles.parent;
        }
        EntityTransportation.default_interactive_area = default_interactive_area;
        function defaultInteractiveArea(transport) {
            return default_interactive_area(TileRectangle.extend(transport.clickable_area, -0.5));
        }
        EntityTransportation.defaultInteractiveArea = defaultInteractiveArea;
        function isLocal(transport) {
            // A shortcut is local if there is no movement action that has a non-relative fixed target
            return transport.type == "door" || !transport.actions.some(a => a.movement.some(m => {
                return !Movement.isLocal(m);
            }));
        }
        EntityTransportation.isLocal = isLocal;
        let Movement;
        (function (Movement) {
            function isLocal(m) {
                // A shortcut is local if there is no movement action that has a non-relative fixed target
                return !(m.fixed_target && !m.fixed_target.relative);
            }
            Movement.isLocal = isLocal;
        })(Movement = EntityTransportation.Movement || (EntityTransportation.Movement = {}));
    })(EntityTransportation = Transportation.EntityTransportation || (Transportation.EntityTransportation = {}));
    /**
     * Coalesces all shortcuts into the general EntityTransportation.
     * More specifically, it transforms door shortcuts into an equivalent {@link GeneralEntityTransportation} to allow unified handling across the code base.
     * Doors are modelled differently in case their handling for pathing is ever changed from the current, hacky variant.
     * @param shortcut
     */
    function normalize(shortcut) {
        if (shortcut.type == "entity")
            return shortcut;
        const off = direction.toVector(shortcut.direction);
        const other = TileCoordinates.move(shortcut.position, off);
        return {
            type: "entity",
            source_loc: shortcut.source_loc,
            entity: { kind: "static", name: shortcut.name },
            clickable_area: TileRectangle.extend(TileRectangle.from(TileCoordinates.move(shortcut.position, Vector2.scale(0.5, off))), 0.5),
            actions: [{
                    cursor: "open",
                    interactive_area: TileArea.fromRect(TileRectangle.from(shortcut.position, other), true),
                    name: `Pass`,
                    movement: [
                        {
                            time: 1,
                            offset: { ...off, level: 0 },
                            valid_from: { origin: shortcut.position },
                            orientation: "bymovement"
                        },
                        {
                            time: 1,
                            offset: { ...direction.toVector(direction.invert(shortcut.direction)), level: 0 },
                            valid_from: { origin: other },
                            orientation: "bymovement"
                        },
                    ]
                }]
        };
    }
    Transportation.normalize = normalize;
    function bounds(shortcut) {
        switch (shortcut.type) {
            case "entity":
                return TileRectangle.lift(Rectangle.combine(shortcut.clickable_area), shortcut.clickable_area.level);
            case "door":
                return TileRectangle.from(shortcut.position, TileCoordinates.move(shortcut.position, direction.toVector(shortcut.direction)));
        }
    }
    Transportation.bounds = bounds;
    function position(shortcut) {
        switch (shortcut.type) {
            case "entity":
                return TileRectangle.center(shortcut.clickable_area);
            case "door":
                return TileCoordinates.move(shortcut.position, Vector2.scale(0.5, direction.toVector(shortcut.direction)));
        }
    }
    Transportation.position = position;
    function name(shortcut) {
        switch (shortcut.type) {
            case "entity":
                return shortcut.entity.name;
            case "door":
                return shortcut.name;
        }
    }
    Transportation.name = name;
    function transform(transport, transform) {
        switch (transport.type) {
            case "door":
                return {
                    type: "door",
                    source_loc: transport.source_loc,
                    name: transport.name,
                    position: TileCoordinates.transform(transport.position, transform),
                    direction: direction.transform(transport.direction, transform.matrix),
                };
            case "entity":
                return {
                    type: "entity",
                    source_loc: transport.source_loc,
                    entity: transport.entity,
                    clickable_area: TileRectangle.transform(transport.clickable_area, transform),
                    actions: transport.actions.map((a) => ({
                        cursor: a.cursor,
                        interactive_area: a.interactive_area ? TileArea.transform(a.interactive_area, transform) : undefined,
                        name: a.name,
                        movement: a.movement.map(movement => {
                            return {
                                time: movement.time,
                                valid_from: movement.valid_from
                                    ? TileArea.transform(movement.valid_from, transform)
                                    : undefined,
                                offset: movement.offset
                                    ? EntityActionMovement.Offset.transform(movement.offset, transform.matrix)
                                    : undefined,
                                fixed_target: movement.fixed_target
                                    ? (movement.fixed_target.relative
                                        ? { target: TileArea.transform(TileArea.normalize(movement.fixed_target.target), transform), relative: true }
                                        : movement.fixed_target)
                                    : undefined,
                                orientation: movement.orientation,
                                forced_orientation: movement.forced_orientation
                                    ? (movement.forced_orientation.relative ? {
                                        dir: direction.transform(movement.forced_orientation.dir, transform.matrix),
                                        relative: true,
                                    } : movement.forced_orientation)
                                    : undefined,
                            };
                        }),
                    })),
                };
        }
    }
    Transportation.transform = transform;
})(Transportation || (Transportation = {}));
